/**
 * Forecast input for one cycle and Business Unit (f11.1), after the
 * prototype's `forecast-grid.html`: page head with the unit selector and the
 * save indicator, filters, the grid, the legend and four stat cards.
 *
 * Route `/cycles/:cycleId/forecast?bu=<unitId>`. Every figure is the server's:
 * rows are scope-filtered, `can_edit` is per row, totals are summed over the
 * visible lines, and calculated values are the stored engine outputs.
 *
 * f11.6: the amber validation banner sits above the grid; its chips and the
 * "only warnings" toggle filter the loaded lines client-side, after the
 * toolbar filters. Warnings never block a save and never use the toast.
 */

import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Ban, Search } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { ForecastGrid, ForecastLine } from '@/api/client';
import { ExportCycleCsvButton } from '@/features/cycle-export/ExportCycleCsvButton';
import { LiveIndicator } from '@/live/LiveIndicator';
import { useLiveCycle, useLiveFallback } from '@/live/useLiveCycle';
import { FrozenBanner } from '@/routes/cycles/freeze/FrozenBanner';
import { RecordUnavailable } from '@/ui/RecordUnavailable';
import '@/styles/forecast.css';

import { AddElementButton } from './add-element/AddElementButton';
import { ForecastGridTable } from './ForecastGridTable';
import {
  NO_FILTERS,
  clientOptions,
  closedSpan,
  cycleLabel,
  distinct,
  filterLines,
  formatCurrency,
  formatMandays,
} from './forecastView';
import type { GridFilters } from './forecastView';
import { ACTUAL_LABEL } from './monthWindow';
import { useForecastGrid, useForecastUnits } from './useForecastGrid';
import { conflictLine, isTimeWindowRefusal, useUpdateForecastLine } from './useUpdateForecastLine';
import type { CellChange } from './useUpdateForecastLine';
import { ValidationBanner } from './validation/ValidationBanner';
import { applyWarningFilter, useWarningFilter } from './validation/useWarningFilter';

type SaveState = 'saved' | 'saving' | 'error';

interface Notice {
  message: string;
  /** f11.2: a confirmation. f11.5: a hard time-window refusal, never a warning. */
  tone?: 'success' | 'error';
  /** Set for a version conflict: the change to resend against the new version. */
  reapply?: { lineId: string; change: CellChange };
}

const NOTICE_CLASS: Record<NonNullable<Notice['tone']> | 'warning', string> = {
  success: 'alert-success',
  error: 'alert-danger',
  warning: 'alert-warning',
};

const SAVE_TEXT: Record<SaveState, string> = {
  saved: 'Saved automatically',
  saving: 'Saving…',
  error: 'Not saved',
};

function StatCards({ grid }: { grid: ForecastGrid }) {
  const fixedLines = grid.lines.filter((line) => line.overwrite_fixed_service).length;
  return (
    <div className="stat-grid fg-stats">
      <div className="stat">
        <div className="stat-label">FY {grid.cycle.period_year} revenue</div>
        <div className="stat-value">{formatCurrency(grid.totals.fy_revenue)}</div>
        <div className="stat-foot">{grid.lines.length} visible lines</div>
      </div>
      <div className="stat">
        <div className="stat-label">FY mandays</div>
        <div className="stat-value">{formatMandays(grid.totals.fy_mandays)}</div>
        <div className="stat-foot">Billable days × weighted FTE</div>
      </div>
      <div className="stat">
        <div className="stat-label">Total managed services</div>
        <div className="stat-value">{formatCurrency(grid.totals.fy_fixed_service)}</div>
        <div className="stat-foot">{fixedLines} lines on fixed service</div>
      </div>
      <div className="stat">
        <div className="stat-label">Revenue adjustments</div>
        <div className="stat-value">{formatCurrency(grid.totals.fy_revenue_adjustment)}</div>
        <div className="stat-foot">Reporting {grid.business_unit.reporting_currency_code}</div>
      </div>
    </div>
  );
}

function Legend({ grid }: { grid: ForecastGrid }) {
  const closed = closedSpan(grid);
  return (
    <div className="legend">
      <span className="legend-item"><span className="swatch sw-ro" /> Read-only identity (Kimble)</span>
      <span className="legend-item"><span className="swatch sw-edit" /> Editable</span>
      <span className="legend-item"><span className="swatch sw-calc" /> Calculated by the engine</span>
      {closed && (
        <span className="legend-item">
          <span className="swatch sw-closed" /> Closed month · {ACTUAL_LABEL} — {closed}
        </span>
      )}
    </div>
  );
}

function Toolbar({
  lines,
  grid,
  filters,
  onChange,
}: {
  lines: readonly ForecastLine[];
  grid: ForecastGrid;
  filters: GridFilters;
  onChange: (next: GridFilters) => void;
}) {
  const clients = clientOptions(lines);
  const products = distinct(lines.map((line) => line.product));
  return (
    <div className="toolbar">
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          type="search"
          placeholder="Search element, account or project"
          aria-label="Search element, account or project"
          value={filters.search}
          onChange={(event) => onChange({ ...filters, search: event.target.value })}
        />
      </div>
      <select aria-label="Client" value={filters.client} onChange={(e) => onChange({ ...filters, client: e.target.value })}>
        <option value="">All clients</option>
        {clients.map((client) => <option key={client.id} value={client.id}>{client.label}</option>)}
      </select>
      <select aria-label="Status" value={filters.status} onChange={(e) => onChange({ ...filters, status: e.target.value })}>
        <option value="">All statuses</option>
        {grid.lookups.statuses.map((status) => <option key={status.code} value={status.code}>{status.name}</option>)}
      </select>
      <select aria-label="Product" value={filters.product} onChange={(e) => onChange({ ...filters, product: e.target.value })}>
        <option value="">All products</option>
        {products.map((product) => <option key={product} value={product}>{product}</option>)}
      </select>
      <div className="spacer" />
      <ExportCycleCsvButton cycleId={grid.cycle.id} stateCode={grid.cycle.state.code} size="sm" />
    </div>
  );
}

export function ForecastGridPage() {
  const { cycleId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const units = useForecastUnits(cycleId);
  const unitId = params.get('bu') ?? units.data?.business_units[0]?.id ?? '';
  // f12.2: pushed figures land in the grid's cache; f11.1's 2 s polling runs
  // only while the stream has been down for more than 5 s.
  const live = useLiveCycle({ cycleId, view: 'grid', businessUnitId: unitId });
  const fallback = useLiveFallback(live.status);
  const gridQuery = useForecastGrid(cycleId, unitId, { poll: fallback });
  const mutation = useUpdateForecastLine(cycleId, unitId);
  // f11.4: `?client=` (entry route B) only PRESELECTS the client filter; the
  // user may clear it, and scope stays the server's (BR-F11.4-4).
  const [filters, setFilters] = useState<GridFilters>(() => ({ ...NO_FILTERS, client: params.get('client') ?? '' }));
  const [inFlight, setInFlight] = useState(0);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const warningFilter = useWarningFilter();

  const grid = gridQuery.data;
  // An unknown `client` value leaves the filter at "All clients".
  const effective = useMemo(
    () =>
      grid && filters.client && !grid.lines.some((line) => line.client_id === filters.client)
        ? { ...filters, client: '' }
        : filters,
    [grid, filters],
  );
  const visible = useMemo(
    () => (grid ? applyWarningFilter(filterLines(grid.lines, effective), warningFilter.filter) : []),
    [grid, effective, warningFilter.filter],
  );

  function save(line: ForecastLine, change: CellChange) {
    setInFlight((n) => n + 1);
    mutation.mutate(
      { line, change },
      {
        onSuccess: () => setFailed(false),
        onError: (error) => {
          setFailed(true);
          if (conflictLine(error)) {
            setNotice({ message: error.message, reapply: { lineId: line.id, change } });
          } else if (isTimeWindowRefusal(error)) {
            setNotice({ message: error.message, tone: 'error' });
          } else {
            setNotice({ message: error instanceof ApiError ? error.message : 'The change could not be saved.' });
          }
        },
        onSettled: () => setInFlight((n) => Math.max(0, n - 1)),
      },
    );
  }

  function reapply() {
    const pending = notice?.reapply;
    const current = pending && grid?.lines.find((line) => line.id === pending.lineId);
    setNotice(null);
    if (pending && current) save(current, pending.change);
  }

  if (units.isError || gridQuery.isError) {
    return (
      <RecordUnavailable
        error={units.error ?? gridQuery.error}
        backTo="/cycles"
        backLabel="Back to cycles"
        onRetry={() => void (units.isError ? units.refetch() : gridQuery.refetch())}
      />
    );
  }

  if (units.data && units.data.business_units.length === 0) {
    return (
      <div className="empty">
        <h3>No forecast lines to show</h3>
        <p>This cycle has no forecast lines in your scope yet.</p>
      </div>
    );
  }

  if (!grid) return <div className="hint">Loading the forecast grid…</div>;

  const saveState: SaveState = inFlight > 0 ? 'saving' : failed ? 'error' : 'saved';

  return (
    <>
      <div className="page-head">
        <div>
          <nav className="fg-crumb" aria-label="Breadcrumb">
            <Link to="/cycles">Cycles</Link> <span>/</span>
            <Link to={`/cycles/${encodeURIComponent(cycleId)}?bu=${encodeURIComponent(unitId)}`}>{grid.cycle.code}</Link> <span>/</span>
            <b>Forecast input</b>
          </nav>
          <h1 className="page-title">{grid.business_unit.name}</h1>
          <div className="page-sub">
            Cycle <span className="mono">{grid.cycle.code}</span> · {cycleLabel(grid.cycle.period_year, grid.cycle.closing_month)} ·{' '}
            {grid.lines.length} delivery elements · reporting {grid.business_unit.reporting_currency_code}
          </div>
        </div>
        <div className="page-actions">
          <LiveIndicator live={live} compact />
          <select
            className="fg-unit-select"
            aria-label="Business Unit"
            value={unitId}
            onChange={(event) => {
              setFilters(NO_FILTERS);
              warningFilter.reset();
              setParams({ bu: event.target.value });
            }}
          >
            {units.data?.business_units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name} ({unit.line_count})
              </option>
            ))}
          </select>
          <AddElementButton
            grid={grid}
            onAdded={(code) => setNotice({ message: `${code} added as a manual line`, tone: 'success' })}
          />
          {grid.editable && (
            <span className="fg-save" data-state={saveState} role="status">
              <span className="fg-save-dot" /> {SAVE_TEXT[saveState]}
            </span>
          )}
        </div>
      </div>

      <FrozenBanner
        stateCode={grid.cycle.state.code}
        snapshot={grid.cycle.current_snapshot}
        reopened={grid.cycle.reopened}
      />

      <Toolbar lines={grid.lines} grid={grid} filters={effective} onChange={setFilters} />

      <ValidationBanner
        summary={grid.validation_summary}
        filter={warningFilter.filter}
        onToggleCode={warningFilter.toggleCode}
        onOnlyWarnings={warningFilter.setOnlyWarnings}
      />

      <div className="card fg-card">
        <ForecastGridTable grid={grid} lines={visible} onSave={save} />
      </div>

      <div className="fg-foot">
        <Legend grid={grid} />
        <span className="hint">
          Showing {visible.length} of {grid.lines.length} lines · scroll right for fixed service and totals
        </span>
      </div>

      <StatCards grid={grid} />

      {notice && (
        <div className={`alert ${NOTICE_CLASS[notice.tone ?? 'warning']} fg-toast`} role="alert">
          {notice.tone === 'error' && <Ban className="fg-deny-icon" aria-hidden="true" />}
          <div>
            <div className="a-title">{notice.message}</div>
            <div className="fg-toast-actions">
              {notice.reapply && (
                <button type="button" className="btn btn-primary btn-sm" onClick={reapply}>
                  Reapply my change
                </button>
              )}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNotice(null)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
